/**
 * Date utility functions to handle timezone-agnostic dates
 * for scheduling (prevent timezone shifts)
 */

/**
 * A bare calendar day: no time, no zone.
 *
 * ⚠️ `new Date('2026-01-09')` parses this as UTC MIDNIGHT. Anywhere west of
 * Greenwich that is the evening of the 8th, so both reading the day back out and
 * formatting it produce the day BEFORE the one stored.
 *
 * Owner, 2026-09-14, on a scanned receipt: "You click edit and it has the
 * correct date but the actual expense ledger recording has 1 day earlier … it
 * should be the 9th and the 2nd which is what is shows when you click edit".
 * The edit form was right because it shows the raw string; every list was a day
 * early.
 *
 * Not cosmetic. Expense.date is DATEONLY and decides which month, quarter and
 * TAX YEAR a cost lands in — a purchase on 1 January shown as 31 December is in
 * the wrong year on a Schedule C. There are 122 DATEONLY columns in the models.
 */
const DATE_ONLY = /^\d{4}-\d{2}-\d{2}$/;

/**
 * `new Date(value)` for display, minus the off-by-one: a bare "YYYY-MM-DD" is
 * built in LOCAL time so it stays on its own day; anything else (a real
 * timestamp) parses exactly as `new Date()` would.
 *
 * Owner, 2026-10-20: invoice email said "Due Date: November 19, 2026", the
 * View & Pay page said "Due: November 18, 2026" (Job.invoiceDueDate is DATEONLY).
 */
export function parseDisplayDate(value: string): Date {
  if (DATE_ONLY.test(value)) {
    const [y, m, d] = value.split('-').map(Number);
    return new Date(y, m - 1, d);
  }
  return new Date(value);
}

/**
 * Convert a date string to YYYY-MM-DD format for date inputs
 * Handles timezone issues by using local date components
 */
export function toDateInputValue(dateString: string | null | undefined): string {
  if (!dateString) return '';

  // Already a calendar day — hand it straight back. Round-tripping it through
  // Date() is what shifted it, in the function whose whole job is not shifting it.
  if (DATE_ONLY.test(dateString)) return dateString;

  try {
    const date = new Date(dateString);
    // Use local date components to avoid timezone shifts
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  } catch {
    return '';
  }
}

/**
 * Convert a date input value (YYYY-MM-DD) to ISO string for API
 * Ensures the date is sent as local midnight, not UTC
 */
export function fromDateInputValue(dateValue: string | null | undefined): string | null {
  if (!dateValue) return null;

  try {
    // Create date at local midnight
    const [year, month, day] = dateValue.split('-').map(Number);
    const date = new Date(year, month - 1, day, 0, 0, 0, 0);
    return date.toISOString();
  } catch {
    return null;
  }
}

/**
 * Format a date for display (locale-aware)
 */
export function formatDateForDisplay(
  dateString: string | null | undefined,
  opts?: Intl.DateTimeFormatOptions
): string {
  if (!dateString) return 'Not set';

  try {
    if (DATE_ONLY.test(dateString)) {
      // Built from the parts in LOCAL time, so no conversion can happen.
      const [y, m, d] = dateString.split('-').map(Number);
      return new Date(y, m - 1, d).toLocaleDateString(undefined, opts);
    }
    // A real instant. Converting to local is CORRECT here — a payment taken at
    // 23:30 UTC genuinely did happen on a different local day, and forcing UTC
    // would introduce the mirror image of the bug above.
    const date = new Date(dateString);
    if (Number.isNaN(date.getTime())) return 'Invalid date';
    return date.toLocaleDateString(undefined, opts);
  } catch {
    return 'Invalid date';
  }
}

/**
 * Format a 24h time string (HH:MM) to 12-hour format (e.g. "2:00 PM")
 */
export function formatTime12h(time: string | null | undefined): string {
  if (!time) return '';
  const [h, m] = time.split(':').map(Number);
  if (isNaN(h) || isNaN(m)) return time;
  const period = h >= 12 ? 'PM' : 'AM';
  const hour12 = h > 12 ? h - 12 : h === 0 ? 12 : h;
  return `${hour12}:${String(m).padStart(2, '0')} ${period}`;
}

/**
 * THE shared time-of-day formatter. Use this for every user-facing time.
 *
 * Always 12-hour with AM/PM, never seconds: "2:30 PM".
 *
 * WHY: time display had drifted into three different behaviours across the app.
 *   - `toLocaleTimeString()` with no arguments returns the USER'S LOCALE format
 *     AND includes seconds — "2:30:45 PM" for en-US, but "14:30:45" for en-GB,
 *     de-DE, fr-FR and most of the world. That is where military time was
 *     leaking in: not from anyone choosing it, but from never specifying.
 *   - `toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })` passes an
 *     EMPTY locale array, which again means "whatever the browser is set to", so
 *     the same screen renders 12-hour for one user and 24-hour for another.
 *   - A handful of places did pass `hour12: true` and were correct all along.
 *
 * Passing 'en-US' with hour12 pins the format so every user sees the same thing
 * regardless of browser locale. `hour: 'numeric'` gives "2:30 PM" rather than
 * "02:30 PM".
 *
 * NOTE: this is deliberately NOT locale-adaptive. If the app is ever localised
 * (Spanish is on the roadmap), this is the single place to revisit — which is
 * the point of routing every caller through it.
 *
 * Accepts a Date, an ISO/datetime string, or an "HH:MM" / "HH:MM:SS" time
 * string. Returns '' for null/undefined and the raw input for unparseable
 * values, so a bad value never renders "Invalid Date" to a customer.
 */
export function formatTimeOfDay(value: Date | string | null | undefined): string {
  if (value === null || value === undefined || value === '') return '';

  let date: Date;
  if (value instanceof Date) {
    date = value;
  } else {
    // A bare "HH:MM" / "HH:MM:SS" has no date part, so anchor it to one —
    // `new Date("14:30")` is invalid in most engines. Rebuild from the captured
    // groups rather than padding the whole string: padStart on "9:05:00" is a
    // no-op (already 7 chars) and would leave a 1-digit hour, which is itself
    // invalid. Only the HOUR needs padding.
    const timeOnly = value.match(/^(\d{1,2}):(\d{2})(?::(\d{2}))?$/);
    date = timeOnly
      ? new Date(`2000-01-01T${timeOnly[1].padStart(2, '0')}:${timeOnly[2]}:${timeOnly[3] || '00'}`)
      : new Date(value);
  }

  if (isNaN(date.getTime())) return typeof value === 'string' ? value : '';

  return date.toLocaleTimeString('en-US', {
    hour: 'numeric',
    minute: '2-digit',
    hour12: true,
  });
}

/**
 * Date and time together: "Aug 13, 2026, 2:30 PM". Same pinning rules as
 * formatTimeOfDay — see there for why the locale is explicit.
 */
export function formatDateTimeForDisplay(value: Date | string | null | undefined): string {
  if (value === null || value === undefined || value === '') return '';
  const date = value instanceof Date ? value : new Date(value);
  if (isNaN(date.getTime())) return typeof value === 'string' ? value : '';
  return `${date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}, `
    + formatTimeOfDay(date);
}

/**
 * Convert a time string to HH:MM format for <input type="time">.
 * Handles: "14:30", "2:30 PM", "11:19 AM", or full datetime strings.
 */
export function toTimeInputValue(timeStr: string | null | undefined): string {
  if (!timeStr) return '';

  // Already in HH:MM 24h format
  const hhmm = timeStr.match(/^(\d{1,2}):(\d{2})$/);
  if (hhmm) return `${hhmm[1].padStart(2, '0')}:${hhmm[2]}`;

  // 12-hour format: "11:19 AM" or "2:30 PM"
  const ampm = timeStr.match(/(\d{1,2}):(\d{2})\s*(AM|PM)/i);
  if (ampm) {
    let h = parseInt(ampm[1]);
    const m = ampm[2];
    if (ampm[3].toUpperCase() === 'PM' && h !== 12) h += 12;
    if (ampm[3].toUpperCase() === 'AM' && h === 12) h = 0;
    return `${h.toString().padStart(2, '0')}:${m}`;
  }

  // Full datetime string fallback
  try {
    const date = new Date(timeStr);
    if (isNaN(date.getTime())) return '';
    const hours = String(date.getHours()).padStart(2, '0');
    const minutes = String(date.getMinutes()).padStart(2, '0');
    if (hours === '00' && minutes === '00') return '';
    return `${hours}:${minutes}`;
  } catch {
    return '';
  }
}

/**
 * A stored instant -> the "YYYY-MM-DDTHH:MM" a datetime-local input shows, in
 * the viewer's own time. `toISOString().slice(0, 16)` gave the UTC clock, so a
 * 2:00 PM follow-up opened for editing as 6:00 PM.
 */
export function toDateTimeLocalValue(value: Date | string | null | undefined): string {
  if (!value) return '';
  const d = value instanceof Date ? value : new Date(value);
  if (isNaN(d.getTime())) return '';
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

/**
 * A datetime-local value ("2026-10-07T14:00", no zone) -> a real ISO instant.
 * Sent raw, the server (UTC) reads it as 14:00 UTC = 10:00 AM Eastern.
 */
export function fromDateTimeLocalValue(value: string | null | undefined): string | null {
  if (!value) return null;
  const d = new Date(value); // the browser reads a zone-less date-time as LOCAL
  return isNaN(d.getTime()) ? null : d.toISOString();
}

/**
 * Get today's date in YYYY-MM-DD format
 */
export function getTodayDateInputValue(): string {
  const today = new Date();
  const year = today.getFullYear();
  const month = String(today.getMonth() + 1).padStart(2, '0');
  const day = String(today.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
}
